import API from '@/services/api/api';
import {
  PlayerDetails,
  RosterPlayer,
  Coach,
  CoachDetails,
  ScheduledGames,
  PublicationSettings,
  Opponent,
  Sport,
  publicationPage,
  ScheduleEventsGetters,
} from '@/store/client/types';
import axios from 'axios';
import { DateTime } from 'luxon';

interface PagedRecords<T> {
  items: T[];
  total: number;
  page: number;
  pages: number;
}

interface CursorPagedRecords<T> {
  items: T[];
  after: string;
  before: string;
}

const fetchClient = async (clientId: number) => {
  try {
    const response = await API.get(`clients/${clientId}`);
    return response.data;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

const fetchSports = async (params: { clientId: number }) => {
  const clientId = params.clientId;
  if (!clientId) return null;
  try {
    const response = await API.get(`clients/${clientId}/sports`);
    return response.data;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

const fetchSport = async (params: { clientId: number; clientSportId: number }): Promise<Sport> => {
  const { clientId, clientSportId } = params;
  if (clientSportId == 0) return null;
  try {
    const response = await API.get(`clients/${clientId}/sports/${clientSportId}`);
    return response.data;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

interface FetchStoriesParams {
  clientId: number;
  page?: number;
  perPage?: number;
  sportId?: Nullable<number>;
  search?: string;
}

const fetchStories = async (params: FetchStoriesParams) => {
  const { clientId, page, perPage, sportId, search } = params;
  try {
    const response = await API.get(`clients/${clientId}/stories`, {
      params: { PageIndex: page, PageSize: perPage, sportId, search },
    });
    return response.data;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

const fetchStory = async (params: { clientId: number; storyId: string }) => {
  const { clientId, storyId } = params;
  try {
    const response = await API.get(`clients/${clientId}/stories/${storyId}`);
    return response.data;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

const fetchSportEvents = async (params: { clientId: number; sportId: number }) => {
  const clientId = params.clientId;
  const sportId = params.sportId;
  if (!clientId || !sportId) return null;
  try {
    const response = await API.get(`clients/${clientId}/sports/${sportId}/events`);
    return response.data;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

const fetchScheduleResultsEvents = async (
  params: {
    clientId: number;
    clientSportId: number;
    hardFetch?: boolean;
  },
  failGracefully = true
) => {
  const { clientId, clientSportId, hardFetch = false } = params;
  try {
    const { data } = await API.get<CursorPagedRecords<ScheduleEventsGetters>>(
      `clients/${clientId}/sports/${clientSportId}/eventresults`,
      {
        params: { PageSize: 9999, hardFetch },
      }
    );
    return data.items;
  } catch (e) {
    if (failGracefully) {
      return [] as ScheduleEventsGetters[];
    }
    throw e;
  }
};

const fetchScheduleUpcomingEvents = async (
  params: {
    clientId: number;
    clientSportId: number;
    hardFetch?: boolean;
  },
  failGracefully = true
) => {
  const { clientId, clientSportId, hardFetch = false } = params;
  try {
    const { data } = await API.get<CursorPagedRecords<ScheduleEventsGetters>>(
      `clients/${clientId}/sports/${clientSportId}/upcomingevents`,
      {
        params: { PageSize: 9999, hardFetch },
      }
    );
    return data.items;
  } catch (e) {
    if (failGracefully) {
      return [] as ScheduleEventsGetters[];
    }
    throw e;
  }
};

const fetchRosterPlayers = async (params: { clientId: number; rosterId: number }): Promise<RosterPlayer[]> => {
  const { clientId, rosterId } = params;
  try {
    const { data } = await API.get<PagedRecords<RosterPlayer>>(`clients/${clientId}/rosters/${rosterId}/players`, {
      params: { PageSize: 9999 },
    });
    return data.items;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

const fetchPlayers = async (params: {
  clientId: number;
  playersIds: number[];
  hardFetch?: boolean;
}): Promise<Record<number, PlayerDetails>> => {
  const { clientId, playersIds, hardFetch = false } = params;
  const query = playersIds.map((id) => `playerId=${id}`).join('&');
  try {
    const { data } = await API.get<Record<number, PlayerDetails>>(
      `clients/${clientId}/players?${query}&hardFetch=${hardFetch}`
    );
    return data;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

const fetchSportCoaches = async (params: { clientId: number; clientSportId: number }): Promise<CoachDetails[]> => {
  const { clientId, clientSportId } = params;
  try {
    const { data } = await API.get<PagedRecords<CoachDetails>>(`clients/${clientId}/sports/${clientSportId}/coaches`, {
      params: { PageSize: 9999 },
    });
    return data.items;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

const fetchCoach = async (params: { clientId: number; coachId: number }): Promise<CoachDetails> => {
  const { clientId, coachId } = params;
  try {
    const { data } = await API.get<CoachDetails>(`clients/${clientId}/coaches/${coachId}`);
    return data;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

const fetchCoaches = async (params: { clientId: number; clientSportId: number }): Promise<Coach[]> => {
  const { clientId, clientSportId } = params;
  try {
    const { data } = await API.get<PagedRecords<Coach>>(`clients/${clientId}/sports/${clientSportId}/coaches`, {
      params: { PageSize: 9999 },
    });
    return data.items;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

const fetchCoachesMembers = async (params: {
  clientId: number;
  membersIds: number[];
}): Promise<Record<number, CoachDetails>> => {
  const { clientId, membersIds } = params;
  const query = membersIds.map((id) => `coachesId=${id}`).join('&');
  try {
    const { data } = await API.get<Record<number, CoachDetails>>(`clients/${clientId}/coaches?${query}`);
    return data;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

const fetchScheduleEventInfo = async (params: { clientId: string; eventId: number }): Promise<ScheduledGames> => {
  const { eventId, clientId } = params;
  if (eventId == 0) return null;

  try {
    const { data } = await API.get<ScheduledGames>(`/clients/${clientId}/schedule/games/${eventId}`);
    return data;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};
const fetchOpponentInfo = async (params: { clientId: string; opponentId: number }): Promise<Opponent> => {
  const { opponentId, clientId } = params;
  if (opponentId == 0) return null;

  try {
    const { data } = await API.get(`/clients/${clientId}/schedule/opponents/${opponentId}`);
    return data.items[0];
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};
const fetchGlobalSport = async (params: { globalSportId: number; tenantId: string }) => {
  const { tenantId, globalSportId } = params;

  try {
    const { data } = await API.get(`/nextgen/sports/global/${globalSportId}?tenantId=${tenantId}`);

    return data;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};
const fetchOpponentRosterPlayers = async (params: { rosterId: number; tenantId: string }) => {
  const { tenantId, rosterId } = params;

  try {
    const { data } = await API.get(`/nextgen/players/roster?PageSize=9999&rosterId=${rosterId}&tenantId=${tenantId}`);
    return data.items;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};
const fetchOpponentRosterBio = async (params: {
  rosterPlayerId: number | number[];
  tenantId: string;
  hardFetch?: boolean;
}) => {
  const { tenantId, rosterPlayerId, hardFetch = false } = params;
  const isBatch = Array.isArray(rosterPlayerId);
  let query = '';

  if (isBatch) {
    query = rosterPlayerId.map((id) => `rosterPlayerId=${id}`).join('&');
  } else {
    query = `rosterPlayerId=${rosterPlayerId}`;
  }

  try {
    const { data } = await API.get(`/nextgen/players/roster/bio?${query}&tenantId=${tenantId}&hardFetch=${hardFetch}`);
    return isBatch ? data : data[0];
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};
const fetchOpponentCoaches = async (params: { sportId: number; tenantId: string }): Promise<Coach[]> => {
  const { tenantId, sportId } = params;

  try {
    const { data } = await API.get(`/nextgen/staff/coaches?PageSize=999&sportId=${sportId}&tenantId=${tenantId}`);
    return data.items;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};
const fetchOpponentCoachBio = async (params: { coachId: number; tenantId: string }) => {
  const { tenantId, coachId } = params;
  try {
    const { data } = await API.get(`/nextgen/staff/coaches/bio?coachId=${coachId}&tenantId=${tenantId}`);
    return data;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

const fetchOpponentScheduleResultEvents = async (params: {
  sportId: number;
  tenantId: string;
  hardFetch?: boolean;
}) => {
  const { tenantId, sportId, hardFetch = false } = params;

  try {
    const { data } = await API.get(
      `/nextgen/events/results?PageSize=9999&sportId=${sportId}&tenantId=${tenantId}&hardFetch=${hardFetch}`
    );
    return data.items ?? [];
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

const fetchOpponentScheduleUpcomingEvents = async (params: {
  sportId: number;
  tenantId: string;
  hardFetch?: boolean;
}) => {
  const { tenantId, sportId, hardFetch = false } = params;
  try {
    const { data } = await API.get(
      `/nextgen/events/results/upcoming?PageSize=9999&sportId=${sportId}&tenantId=${tenantId}&hardFetch=${hardFetch}`
    );
    return data.items ?? [];
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

const getOpponentTenantId = async (opponentGlobalId: string | number) => {
  const API2 = axios.create({ baseURL: 'https://services.sidearmsports.com/services' });
  try {
    const { data } = await API2.get(`/globalopponentlookup.ashx?client_school_id=${opponentGlobalId}`);
    return data.schools[0];
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

// const getSupportTenantId = async (opponentGlobalId: string) => {
//   const API2 = axios.create({ baseURL: 'https://services.sidearmsports.com/services' });
//   try {
//     const { data } = await API2.get(`/globalopponentlookup.ashx?client_school_id=${opponentGlobalId}`);
//     return data.schools[0];
//   } catch (e) {
//     return e.response ? Promise.reject(e.response) : e;
//   }
// };

const fetchSupportTeam = async (params: { sportId: number; tenantId: string }): Promise<Coach[]> => {
  const { tenantId, sportId } = params;

  try {
    const { data } = await API.get<PagedRecords<Coach>>(
      `/nextgen/staff/support?PageSize=9999&sportId=${sportId}&tenantId=${tenantId}`,
      {
        params: { $pageSize: 9999 },
      }
    );
    return data.items;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

const fetchPublicationSettings = async (clientId: number): Promise<PublicationSettings> => {
  try {
    const { data } = await API.get(`/clients/${clientId}/publications/settings`);
    return data;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

const fetchPublicationPage = async (s3BucketUri: string): Promise<publicationPage> => {
  const connectionToPublicationFile = axios.create({
    baseURL: `${s3BucketUri}`,
  });
  try {
    const timestamp = DateTime.now().toFormat('yyyyMMddHHmmss');
    const { data } = await connectionToPublicationFile.get(`/data_live.json?v=${timestamp}`);
    return data;
  } catch (e) {
    return e.response ? Promise.reject(e.response) : e;
  }
};

export default {
  fetchClient,
  fetchSports,
  fetchSport,
  fetchStories,
  fetchStory,
  fetchSportEvents,
  fetchScheduleResultsEvents,
  fetchScheduleUpcomingEvents,
  fetchRosterPlayers,
  fetchPlayers,
  fetchSportCoaches,
  fetchCoach,
  fetchCoaches,
  fetchCoachesMembers,
  fetchScheduleEventInfo,
  fetchOpponentInfo,
  fetchGlobalSport,
  fetchOpponentScheduleResultEvents,
  fetchOpponentScheduleUpcomingEvents,
  fetchOpponentRosterPlayers,
  fetchOpponentRosterBio,
  fetchOpponentCoaches,
  fetchOpponentCoachBio,
  getOpponentTenantId,
  fetchSupportTeam,
  fetchPublicationSettings,
  fetchPublicationPage,
};
